import { useState, useEffect, Fragment, useRef, useMemo, useCallback } from "react";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
  Plus,
  RefreshCw,
  Receipt as ReceiptIcon,
  Search,
  User,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { OrganizationSummary } from "../shared/Interfaces";
import { OrganizationPermission, hasPermission } from "../shared/organizationPermissions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { reportHandledError, reportApiError } from "../utils/reportError";

const COMPLETION_WINDOW_MIN = 24;
const COMPLETION_WINDOW_MAX = 7 * 24; // 7 days in hours
const COMPLETION_WINDOW_STEP = 12;
const COMPLETION_WINDOW_MARKS = [
  { value: 24, label: "24h" },
  { value: 48, label: "2d" },
  { value: 72, label: "3d" },
  { value: 96, label: "4d" },
  { value: 120, label: "5d" },
  { value: 144, label: "6d" },
  { value: 168, label: "7d" },
];

export interface RequestCounts {
  total: number;
  completed: number;
  failed: number;
}

export interface BatchErrorSummary {
  code: string;
  message: string;
  message_pt?: string;
  type?: string;
  count: number;
  action?: { label: string; url: string } | null;
}

export interface Batch {
  id: string;
  endpoint: string;
  input_file_id: string;
  completion_window: string;
  output_file_id: string;
  error_file_id: string;
  in_progress_at: number;
  expires_at: number;
  finalizing_at: number;
  completed_at: number;
  failed_at: number;
  cancelling_at: number;
  cancelled_at: number;
  request_counts: RequestCounts;
  created_at: number;
  status: string;
  metadata: { [key: string]: any };
  total_cost: number;
  errors: string[];
  errors_summary?: BatchErrorSummary[];
  created_by_email?: string;
  file_owner_email?: string;
}

interface ListBatchRequestsPlatform {
  object: string;
  data: Batch[];
  first_id: string | null;
  last_id: string | null;
  has_more: boolean;
}

type StatusVariant = "success" | "warning" | "destructive" | "secondary" | "outline";

interface StatusInfo {
  variant: StatusVariant;
  label: string;
}

const getStatusInfo = (status: string): StatusInfo => {
  switch (status) {
    case "pending":
      return { variant: "warning", label: "Pendente" };
    case "in_progress":
      return { variant: "secondary", label: "Em andamento" };
    case "finalizing":
      return { variant: "secondary", label: "Finalizando" };
    case "completed":
      return { variant: "success", label: "Concluído" };
    case "failed":
      return { variant: "destructive", label: "Erro" };
    case "expired":
      return { variant: "warning", label: "Expirado" };
    case "validating":
      return { variant: "secondary", label: "Validando" };
    case "cancelling":
      return { variant: "warning", label: "Cancelando" };
    case "cancelled":
      return { variant: "outline", label: "Cancelado" };
    default:
      return { variant: "outline", label: status };
  }
};

const fmtBytes = (b: number) =>
  b >= 1024 * 1024 ? (b / 1024 / 1024).toFixed(1) + " MB" : (b / 1024).toFixed(1) + " KB";

function parseLegacyErrorString(raw: string): { code?: string; message: string } {
  // Older entries are JSON-encoded {error:{message,type,code,...}} envelopes.
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object") {
      const err = obj.error ?? obj;
      const message = (err.message ?? "").toString();
      if (message) return { code: err.code, message };
    }
  } catch {
    // not JSON — fall through
  }
  return { message: raw };
}

interface ListBatchesProps {
  refresh: boolean;
  refreshing: boolean;
  setRefreshing: (refreshing: boolean) => void;
  billingScope: "personal" | "organization";
  activeOrganizationId: string;
  viewAllOrgBatches: boolean;
}

function ListBatches({
  refresh,
  refreshing,
  setRefreshing,
  billingScope,
  activeOrganizationId,
  viewAllOrgBatches,
}: ListBatchesProps) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const isInitialMount = useRef(true);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 100;
  const [expandedBatch, setExpandedBatch] = useState<string | null>(null);
  const [cancellingBatches, setCancellingBatches] = useState<Set<string>>(new Set());
  const isOrganizationScope = billingScope === "organization";
  type DownloadInfo = {
    percent: number;
    received: number;
    total: number | null;
  };

  const [downloadProgress, setDownloadProgress] = useState<Record<string, DownloadInfo>>({});

  const buildBatchUrl = (
    path: string,
    params: Record<string, string> = {},
    options?: { includeOrganization?: boolean; viewAll?: boolean }
  ) => {
    const { includeOrganization = true, viewAll = false } = options ?? {};
    const search = new URLSearchParams(params);
    if (includeOrganization && billingScope === "organization" && activeOrganizationId) {
      search.set("organization_id", activeOrganizationId);
    }
    if (viewAll && billingScope === "organization" && activeOrganizationId) {
      search.set("view_all", "true");
    }
    const query = search.toString();
    return `${API_BASE}/api${path}${query ? `?${query}` : ""}`;
  };

  const handleDownload = async (fileId: string, filename: string) => {
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;

      setDownloadProgress((p) => ({
        ...p,
        [fileId]: { percent: 0, received: 0, total: null },
      }));

      const downloadUrl = buildBatchUrl(`/files-platform/${fileId}/content`, {}, { viewAll: viewAllOrgBatches });
      const res = await fetch(downloadUrl, {
        headers: { Authorization: `Auth ${accessToken}` },
      });

      if (!res.ok) {
        reportApiError(res, { feature: "batch.download", method: "GET" });
        toast.error("Erro ao baixar o arquivo. Tente novamente.", TOASTPROPS);
        return;
      }

      const total = +res.headers.get("Content-Length")!;
      const reader = res.body!.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (total) {
          setDownloadProgress((p) => ({
            ...p,
            [fileId]: { percent: Math.round((received / total) * 100), received, total },
          }));
        } else {
          setDownloadProgress((p) => ({
            ...p,
            [fileId]: { percent: 0, received, total: null },
          }));
        }
      }

      const blob = new Blob(chunks as BlobPart[]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || `${fileId}.jsonl`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      reportHandledError(err, { category: "api", feature: "batch.download" });
      console.error(err);
    } finally {
      setDownloadProgress((p) => {
        const info = p[fileId];
        return {
          ...p,
          [fileId]: {
            percent: 100,
            received: info?.total ?? info?.received ?? 0,
            total: info?.total ?? info?.received ?? 0,
          },
        };
      });
      setTimeout(
        () =>
          setDownloadProgress((p) => {
            const { [fileId]: _ignore, ...rest } = p;
            return rest;
          }),
        2000
      );
    }
  };

  async function loadMoreBatches(options: { reset?: boolean } = {}) {
    const { reset = false } = options;
    if (loading) return;
    try {
      setLoading(true);
      const paramsObj: { limit: string; after?: string } = {
        limit: itemsPerPage.toString(),
      };
      const afterId = reset ? null : lastBatchId;
      if (afterId) paramsObj.after = afterId;

      const url = buildBatchUrl("/batches-platform", paramsObj, {
        viewAll: viewAllOrgBatches,
      });
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Auth ${access_token}`,
        },
      });

      if (!response.ok) {
        reportApiError(response, { feature: "batch.list", method: "GET" });
        throw new Error(`Request failed with status ${response.status}`);
      }
      const result: ListBatchRequestsPlatform = await response.json();
      if (reset) {
        setBatches(result.data);
      } else {
        setBatches((prev) => [...prev, ...result.data]);
      }
      setHasMore(result.has_more);
      setLastBatchId(result.last_id);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "batch.list" });
      toast.error("Erro ao carregar mais batches. Tente novamente mais tarde.", TOASTPROPS);
    }
    setLoading(false);
  }

  useEffect(() => {
    setBatches([]);
    setHasMore(false);
    setLastBatchId(null);
    loadMoreBatches({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, loginWithRedirect, billingScope, activeOrganizationId, viewAllOrgBatches]);

  useEffect(() => {
    async function refreshBatches() {
      try {
        if (refreshing) return;
        setRefreshing(true);
        const limit = batches.length > 0 ? batches.length : itemsPerPage;
        const paramsObj = { limit: limit.toString() };
        const url = buildBatchUrl("/batches-platform", paramsObj, {
          viewAll: viewAllOrgBatches,
        });
        const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!access_token) return;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Auth ${access_token}`,
          },
        });
        if (response.ok) {
          const result: ListBatchRequestsPlatform = await response.json();
          setBatches(result.data);
          setHasMore(result.has_more);
          setLastBatchId(result.last_id);
        } else {
          reportApiError(response, { feature: "batch.refresh", method: "GET" });
          toast.error("Erro ao atualizar os batches. Tente novamente mais tarde.", TOASTPROPS);
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "batch.refresh" });
        toast.error("Erro ao atualizar os batches. Tente novamente mais tarde.", TOASTPROPS);
      } finally {
        setRefreshing(false);
      }
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    } else {
      refreshBatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const filteredBatches = batches.filter((batch) =>
    batch.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch && trimmedSearch.length === 36) {
      const existing = batches.find(
        (batch) => batch.id.toLowerCase() === trimmedSearch.toLowerCase()
      );
      if (!existing && filteredBatches.length === 0) {
        const fetchBatchById = async () => {
          const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
          if (!access_token) return;
          const url = buildBatchUrl(`/batches-platform/${trimmedSearch}`, {}, {
            viewAll: viewAllOrgBatches,
          });
          const response = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Auth ${access_token}`,
            },
          });
          if (response.ok) {
            const batch = await response.json();
            setBatches((prev) => [{ ...batch, __manuallyAdded: true }, ...prev]);
          } else {
            reportApiError(response, { feature: "batch.search", method: "GET" });
            toast.error("Batch não encontrado. Verifique o ID e tente novamente.", TOASTPROPS);
          }
        };
        fetchBatchById();
      }
    }
  }, [
    searchTerm,
    batches,
    filteredBatches,
    getAccessTokenSilently,
    billingScope,
    activeOrganizationId,
    viewAllOrgBatches,
  ]);

  useEffect(() => {
    if (searchTerm.trim().length !== 36) {
      setBatches((prev) => prev.filter((batch) => !(batch as any).__manuallyAdded));
    }
  }, [searchTerm]);

  const toggleExpansion = (id: string) => {
    setExpandedBatch(expandedBatch === id ? null : id);
  };

  const handleCancelBatch = async (batchId: string) => {
    if (cancellingBatches.has(batchId)) return;

    try {
      setCancellingBatches((prev) => new Set(prev).add(batchId));
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const url = buildBatchUrl(`/batches/${batchId}/cancel`, {}, { includeOrganization: false });

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Auth ${accessToken}`,
        },
      });

      if (response.ok) {
        setBatches((prev) =>
          prev.map((batch) =>
            batch.id === batchId
              ? { ...batch, status: "cancelling", cancelling_at: Math.floor(Date.now() / 1000) }
              : batch
          )
        );
        toast.success("Batch está sendo cancelado.", TOASTPROPS);
      } else {
        reportApiError(response, { feature: "batch.cancel", method: "POST" });
        const errorData = await response.json();
        toast.error(errorData.detail || "Erro ao cancelar o batch. Tente novamente.", TOASTPROPS);
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "batch.cancel" });
      toast.error("Erro ao cancelar o batch. Tente novamente.", TOASTPROPS);
    } finally {
      setCancellingBatches((prev) => {
        const newSet = new Set(prev);
        newSet.delete(batchId);
        return newSet;
      });
    }
  };

  const FileRow = ({ fileId, label }: { fileId: string; label: string }) => {
    if (!fileId) return null;

    const prog = downloadProgress[fileId];

    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="mt-0.5 truncate font-mono text-[12px] text-foreground">{fileId}</div>
        </div>
        {prog ? (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${prog.percent}%` }}
              />
            </div>
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              {fmtBytes(prog.received)} / {prog.percent}%
            </span>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(fileId, `${fileId}.jsonl`);
            }}
            aria-label="Download file"
            title="Download"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>
        )}
      </div>
    );
  };

  const secondsToDate = (seconds: number) => new Date(seconds * 1000);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Buscar por Batch ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] table-fixed text-sm">
            <colgroup>
              <col style={{ width: "32%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3">Batch ID</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Progresso</th>
                <th className="px-5 py-3 text-right">Custo Total</th>
                <th className="px-5 py-3">Criado em</th>
              </tr>
            </thead>
            <tbody>
              {loading && batches.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="h-12 border-b border-border/60">
                    <td className="px-5 py-3"><Skeleton className="max-w-full h-4 w-48" /></td>
                    <td className="px-5 py-3"><Skeleton className="max-w-full h-5 w-24 rounded-full" /></td>
                    <td className="px-5 py-3"><Skeleton className="max-w-full h-4 w-32" /></td>
                    <td className="px-5 py-3"><Skeleton className="max-w-full ml-auto h-4 w-20" /></td>
                    <td className="px-5 py-3"><Skeleton className="max-w-full h-4 w-32" /></td>
                  </tr>
                ))
              ) : filteredBatches.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="h-60 px-5 py-8 text-center">
                    <div className="mx-auto flex max-w-sm flex-col items-center gap-2">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <ReceiptIcon className="h-5 w-5" />
                      </span>
                      <p className="font-semibold text-foreground">Nenhum batch encontrado</p>
                      <p className="text-sm text-muted-foreground">
                        Seus batches de processamento aparecerão aqui após a primeira criação
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredBatches.map((batch) => {
                  const progressPercentage =
                    batch.request_counts.total > 0
                      ? ((batch.request_counts.completed + batch.request_counts.failed) /
                          batch.request_counts.total) *
                        100
                      : 0;
                  const statusInfo = getStatusInfo(batch.status);
                  const isExpanded = expandedBatch === batch.id;
                  const responsibleEmail = batch.created_by_email || batch.file_owner_email;
                  const showFileOwnerRow =
                    Boolean(batch.file_owner_email) &&
                    batch.file_owner_email !== responsibleEmail;
                  const isCancelling = cancellingBatches.has(batch.id);
                  const canCancel =
                    batch.status === "pending" ||
                    batch.status === "in_progress" ||
                    batch.status === "validating";

                  return (
                    <Fragment key={batch.id}>
                      <tr className="border-b border-border/60 transition-colors hover:bg-muted/30">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span className="min-w-0 break-all font-mono text-[12px] text-foreground">
                              {batch.id}
                            </span>
                            <button
                              onClick={() => toggleExpansion(batch.id)}
                              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                              aria-label={isExpanded ? "Recolher" : "Expandir"}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                            {canCancel && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelBatch(batch.id);
                                }}
                                disabled={isCancelling}
                                title="Cancelar batch"
                                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                              >
                                {isCancelling ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <X className="h-3.5 w-3.5" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full bg-primary transition-all"
                                style={{ width: `${progressPercentage}%` }}
                              />
                            </div>
                            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                              {Math.round(progressPercentage)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-[13px] text-foreground">
                          R$ {batch.total_cost.toFixed(4)}
                        </td>
                        <td className="px-5 py-3 text-[13px] text-muted-foreground">
                          {secondsToDate(batch.created_at).toLocaleString()}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="border-b border-border bg-muted/20">
                          <td colSpan={5} className="px-5 py-5">
                            <div className="space-y-5">
                              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                                <div className="flex flex-col">
                                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Endpoint
                                  </dt>
                                  <dd className="font-mono text-[13px] text-foreground">
                                    {batch.endpoint}
                                  </dd>
                                </div>
                                <div className="flex flex-col">
                                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Janela de conclusão
                                  </dt>
                                  <dd className="text-[13px] text-foreground">
                                    {batch.completion_window}
                                  </dd>
                                </div>
                                <div className="flex flex-col">
                                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Total de requisições
                                  </dt>
                                  <dd className="font-mono text-[13px] tabular-nums text-foreground">
                                    {batch.request_counts.total}
                                  </dd>
                                </div>
                                <div className="flex flex-col">
                                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Concluídas
                                  </dt>
                                  <dd className="font-mono text-[13px] tabular-nums text-foreground">
                                    {batch.request_counts.completed}
                                  </dd>
                                </div>
                                <div className="flex flex-col">
                                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    Falhadas
                                  </dt>
                                  <dd className="font-mono text-[13px] tabular-nums text-foreground">
                                    {batch.request_counts.failed}
                                  </dd>
                                </div>
                                {isOrganizationScope && (
                                  <>
                                    <div className="flex flex-col">
                                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                        Responsável
                                      </dt>
                                      <dd className="text-[13px] text-foreground">
                                        {responsibleEmail ?? "Não disponível"}
                                      </dd>
                                    </div>
                                    {showFileOwnerRow && (
                                      <div className="flex flex-col">
                                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                          Arquivo enviado por
                                        </dt>
                                        <dd className="text-[13px] text-foreground">
                                          {batch.file_owner_email}
                                        </dd>
                                      </div>
                                    )}
                                  </>
                                )}
                              </dl>

                              <div className="space-y-2">
                                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                  Arquivos
                                </h4>
                                <div className="space-y-2">
                                  <FileRow fileId={batch.input_file_id} label="Arquivo de entrada" />
                                  {batch.output_file_id && (
                                    <FileRow fileId={batch.output_file_id} label="Arquivo de saída" />
                                  )}
                                  {batch.error_file_id && (
                                    <FileRow fileId={batch.error_file_id} label="Arquivo de erro" />
                                  )}
                                </div>
                              </div>

                              <BatchErrorsBlock batch={batch} />

                              <div className="space-y-2">
                                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                  Metadados
                                </h4>
                                <pre className="overflow-x-auto rounded-md border border-border bg-muted/50 p-3 font-mono text-[12px] text-foreground/90">
                                  {Object.keys(batch.metadata).length > 0
                                    ? JSON.stringify(batch.metadata, null, 2)
                                    : "Nenhum metadado disponível"}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {hasMore && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => loadMoreBatches()} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Carregando...
              </>
            ) : (
              "Carregar mais"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

const BatchErrorsBlock = ({ batch }: { batch: Batch }) => {
  const summary: BatchErrorSummary[] = Array.isArray(batch.errors_summary)
    ? batch.errors_summary
    : [];

  if (summary.length > 0) {
    return (
      <div className="space-y-2" data-testid="batch-errors-section">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Erros
        </h4>
        <div className="space-y-2">
          {summary.map((entry, index) => (
            <div
              key={`${entry.code}-${index}`}
              data-testid="batch-error-item"
              data-error-code={entry.code}
              className="rounded-md border border-destructive/30 bg-destructive/5 p-3"
            >
              <div className="text-[13px] font-medium text-foreground">
                {entry.message_pt && entry.message_pt.trim().length > 0
                  ? entry.message_pt
                  : entry.message}
              </div>
              <div className="mt-1 flex items-center gap-2 text-[12px] text-muted-foreground">
                {entry.count > 0 && (
                  <span data-testid="batch-error-count">
                    {entry.count} requisição{entry.count > 1 ? "ões" : ""} afetada
                    {entry.count > 1 ? "s" : ""}
                  </span>
                )}
                {entry.code && (
                  <span
                    data-testid="batch-error-code"
                    className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px]"
                  >
                    {entry.code}
                  </span>
                )}
              </div>
              {entry.action && entry.action.url && (
                <a
                  href={entry.action.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="batch-error-action"
                  className="mt-1.5 inline-block text-[12px] font-medium text-primary hover:underline"
                >
                  {entry.action.label || "Saiba mais"}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const raw = batch.errors ?? [];
  if (raw.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="batch-errors-section">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Erros
      </h4>
      <div className="space-y-2">
        {raw.map((error, index) => {
          const parsed = parseLegacyErrorString(error);
          return (
            <div
              key={index}
              data-testid="batch-error-item"
              data-error-code={parsed.code ?? "unknown"}
              className="rounded-md border border-destructive/30 bg-destructive/5 p-3"
            >
              <div className="text-[13px] font-medium text-foreground">{parsed.message}</div>
              {parsed.code && (
                <div className="mt-1">
                  <span
                    data-testid="batch-error-code"
                    className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px]"
                  >
                    {parsed.code}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface BatchModalProps {
  openModal: boolean;
  onClose: () => void;
  handleCreateBatch: (fileId: string, completionWindow: string) => Promise<void>;
  creatingBatch: boolean;
  billingScope: "personal" | "organization";
  activeOrganizationId: string;
}

const BatchModal = ({
  openModal,
  onClose,
  handleCreateBatch,
  creatingBatch,
}: BatchModalProps) => {
  const [fileId, setFileId] = useState("");
  const [completionWindowHours, setCompletionWindowHours] = useState(COMPLETION_WINDOW_MAX);

  useEffect(() => {
    if (!openModal) {
      setFileId("");
      setCompletionWindowHours(COMPLETION_WINDOW_MAX);
    }
  }, [openModal]);

  const formatCompletionWindowValue = (hours: number) => {
    if (hours === 24) return "24h";
    if (hours % 24 === 0) return `${hours / 24}d`;
    return `${hours}h`;
  };

  const formatHumanReadableWindow = (hours: number) => {
    if (hours % 24 === 0) {
      const days = hours / 24;
      return `${days} dia${days > 1 ? "s" : ""}`;
    }
    return `${hours} hora${hours > 1 ? "s" : ""}`;
  };

  const formattedCompletionWindow = formatCompletionWindowValue(completionWindowHours);
  const humanReadableWindow = formatHumanReadableWindow(completionWindowHours);

  return (
    <Dialog open={openModal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Criar Novo Batch</DialogTitle>
          <DialogDescription>
            Crie um batch para processar múltiplas requisições de chat de forma eficiente. O
            arquivo deve estar no formato JSONL, ter no máximo 200MB e conter até 50.000
            requisições.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="batch-file-id">File ID</Label>
            <Input
              id="batch-file-id"
              placeholder="Insira o File ID do arquivo JSONL"
              value={fileId}
              onChange={(e) => setFileId(e.target.value)}
              className="font-mono text-[13px]"
              disabled={creatingBatch}
            />
          </div>

          <div className="rounded-md border border-border bg-muted/30 p-3 text-[12px] text-foreground/80">
            <p className="font-semibold text-foreground">Observações importantes:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>
                Suporte apenas para endpoint{" "}
                <code className="rounded bg-background px-1 py-0.5 font-mono text-[11px]">
                  /v1/chat/completions
                </code>
              </li>
              <li>Selecione abaixo a janela de processamento entre 24h e 7 dias</li>
              <li>Economia de 50% nos custos comparado às requisições individuais</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="batch-completion-window">Janela máxima de processamento</Label>
            <input
              id="batch-completion-window"
              type="range"
              min={COMPLETION_WINDOW_MIN}
              max={COMPLETION_WINDOW_MAX}
              step={COMPLETION_WINDOW_STEP}
              value={completionWindowHours}
              onChange={(e) => setCompletionWindowHours(Number(e.target.value))}
              disabled={creatingBatch}
              className="block w-full accent-primary"
            />
            <div className="flex justify-between px-1 text-[10px] font-medium text-muted-foreground">
              {COMPLETION_WINDOW_MARKS.map((m) => (
                <span key={m.value}>{m.label}</span>
              ))}
            </div>
            <p className="text-[12px] text-muted-foreground">
              Tempo máximo de processamento:{" "}
              <strong className="text-foreground">{formattedCompletionWindow}</strong> (
              {humanReadableWindow})
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={creatingBatch}>Cancelar</Button>
            <Button
              onClick={() => handleCreateBatch(fileId.trim(), formattedCompletionWindow)}
              disabled={creatingBatch || !fileId.trim()}
            >
              {creatingBatch ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Criando...
                </>
              ) : (
                "Criar Batch"
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function BatchAPI() {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const [openModal, setOpenModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [creatingBatch, setCreatingBatch] = useState(false);

  const [billingScope, setBillingScope] = useState<"personal" | "organization">("personal");
  const [activeOrganizationId, setActiveOrganizationId] = useState<string>("");
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const [showAllOrgBatches, setShowAllOrgBatches] = useState<boolean>(false);

  const currentUserId = useMemo(() => {
    if (!user?.sub) return "";
    const parts = user.sub.split("|");
    return parts[parts.length - 1] || user.sub;
  }, [user]);

  const memberOrganizations = useMemo(() => {
    return organizations.filter((org) => {
      return Boolean(org.members?.some((member) => member.user_id === currentUserId));
    });
  }, [organizations, currentUserId]);

  const organizationsWithApiAccess = useMemo(() => {
    return memberOrganizations.filter((org) => {
      const membership = org.members?.find((member) => member.user_id === currentUserId);
      if (!membership) return false;
      return hasPermission(membership.permissions, OrganizationPermission.API_KEYS_VIEW_ALL);
    });
  }, [memberOrganizations, currentUserId]);

  const hasOrganizations = organizations.length > 0;
  const hasMemberOrganizations = memberOrganizations.length > 0;
  const hasOrganizationsWithApiAccess = organizationsWithApiAccess.length > 0;
  const currentOrgMembership = useMemo(() => {
    if (!activeOrganizationId) return undefined;
    const org = organizations.find((org) => org.organization_id === activeOrganizationId);
    return org?.members?.find((member) => member.user_id === currentUserId);
  }, [activeOrganizationId, organizations, currentUserId]);

  const canViewAllOrgBatches = Boolean(
    billingScope === "organization" &&
      currentOrgMembership &&
      hasPermission(currentOrgMembership.permissions, OrganizationPermission.BATCH_FILES_VIEW_ALL)
  );

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoadingOrganizations(true);
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;
      const response = await fetch(`${API_BASE}/api/organizations/list`, {
        method: "GET",
        headers: { Authorization: `Auth ${access_token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data["organizations"] ?? []);
      } else {
        reportApiError(response, { feature: "batch.organizations", method: "GET" });
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "batch.organizations" });
      toast.error("Erro ao buscar organizações.", TOASTPROPS);
    } finally {
      setLoadingOrganizations(false);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (billingScope === "organization") {
      if (!hasMemberOrganizations) {
        setBillingScope("personal");
        setActiveOrganizationId("");
      } else if (
        !memberOrganizations.some((org) => org.organization_id === activeOrganizationId)
      ) {
        setActiveOrganizationId(memberOrganizations[0].organization_id);
      }
    }
  }, [billingScope, memberOrganizations, activeOrganizationId, hasMemberOrganizations]);

  useEffect(() => {
    if (billingScope !== "organization" || !activeOrganizationId || !canViewAllOrgBatches) {
      setShowAllOrgBatches(false);
    }
  }, [billingScope, activeOrganizationId, canViewAllOrgBatches]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setCreatingBatch(false);
  };

  const handleCreateBatch = async (rawFileId: string, completionWindow: string) => {
    const fileId = rawFileId.trim();
    if (!fileId || creatingBatch) return;
    setCreatingBatch(true);
    try {
      const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!access_token) return;

      const search = new URLSearchParams();
      if (billingScope === "organization" && activeOrganizationId) {
        search.set("organization_id", activeOrganizationId);
      }
      const query = search.toString();
      const url = `${API_BASE}/api/batches-platform${query ? `?${query}` : ""}`;

      const windowValue = completionWindow || "7d";

      const body = {
        input_file_id: fileId,
        endpoint: "/v1/chat/completions",
        completion_window: windowValue,
        metadata: {},
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Auth ${access_token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        handleCloseModal();
        setRefresh((prev) => !prev);
        toast.success("Batch criado com sucesso!", TOASTPROPS);
        return;
      }

      reportApiError(response, { feature: "batch.create", method: "POST" });
      toast.error("Erro ao criar o batch. Verifique o File ID e tente novamente.", TOASTPROPS);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "batch.create" });
      toast.error("Erro ao criar o batch. Verifique o File ID e tente novamente.", TOASTPROPS);
    } finally {
      setCreatingBatch(false);
    }
  };

  const handleScopeChange = (value: string) => {
    if (value === "personal") {
      setBillingScope("personal");
      setActiveOrganizationId("");
    } else {
      setBillingScope("organization");
      setActiveOrganizationId(value);
    }
  };

  const scopeSelectValue =
    billingScope === "personal" ? "personal" : activeOrganizationId || "";
  const isOrganizationScope = billingScope === "organization";

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex w-full flex-col items-start gap-3 lg:w-[400px]">
          <Label className="text-[12px] text-muted-foreground">Gerenciar batches de:</Label>
          {loadingOrganizations ? (
            <Skeleton className="h-10 w-full rounded-lg" />
          ) : (
            <>
              <Select value={scopeSelectValue || "personal"} onValueChange={handleScopeChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Minha conta pessoal
                    </span>
                  </SelectItem>
                  {memberOrganizations.map((org) => (
                    <SelectItem key={org.organization_id} value={org.organization_id}>
                      <span className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {org.name || org.organization_id}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {billingScope === "organization" && canViewAllOrgBatches && (
                <Button
                  variant={showAllOrgBatches ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowAllOrgBatches((prev) => !prev)}
                >
                  {showAllOrgBatches ? "Ver apenas meus batches" : "Ver todos da org"}
                </Button>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setRefresh((prev) => !prev)} disabled={refreshing}>
            {refreshing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Atualizar
          </Button>
          <Button onClick={() => setOpenModal(true)}>
            <Plus className="h-3.5 w-3.5" />
            Criar Batch
          </Button>
        </div>
      </div>

      <ListBatches
        refresh={refresh}
        setRefreshing={setRefreshing}
        refreshing={refreshing}
        billingScope={billingScope}
        activeOrganizationId={activeOrganizationId}
        viewAllOrgBatches={showAllOrgBatches}
      />

      {!loadingOrganizations && !hasOrganizations && (
        <p className="text-[12px] text-muted-foreground">
          Crie uma organização para compartilhar batches com sua equipe.
        </p>
      )}
      {!loadingOrganizations && hasOrganizations && !hasOrganizationsWithApiAccess && (
        <p className="text-[12px] text-muted-foreground">
          Você não possui permissões de API em nenhuma organização. Use sua conta pessoal para
          gerenciar batches.
        </p>
      )}

      <BatchModal
        openModal={openModal}
        onClose={handleCloseModal}
        handleCreateBatch={handleCreateBatch}
        creatingBatch={creatingBatch}
        billingScope={billingScope}
        activeOrganizationId={activeOrganizationId}
      />
    </div>
  );
}
